/**
 * Admin Content Router
 * Renders the appropriate tab content based on activeTab
 */
import { memo, lazy, Suspense } from 'react';
import { usePermissions } from '@/context/PermissionsContext';

// Analytics & Reporting
import { 
  AnalyticsDashboard,
  EnhancedAnalytics,
  AdvancedAnalyticsDashboard,
  CampaignAnalyticsDashboard,
  RealTimeAnalytics,
  SiteStatisticsTab,
  ComparativeReportingTab,
  ProfitabilityTab,
  AutomatedReportsTab,
  AccountingExportTab,
  CartRecoveryTab,
  UnifiedAnalytics,
  DailyInsightsDashboard,
  RoyaltyReportTab
} from '@/components/admin/analytics';
import AeoChecklistTab from '@/components/admin/AeoChecklistTab';
import BlogTab from '@/components/admin/BlogTab';
import GoogleReviewsTab from '@/components/admin/GoogleReviewsTab';

// Orders & Kitchen
import {
  OrdersTab,
  KitchenDisplayTab,
  ReceiptSettingsTab,
  CashDrawerTab,
  TableStatusTab,
  PrinterRoutingTab
} from '@/components/admin/orders';

// Menu Reordering
import MenuReorderTab from '@/components/admin/menu/MenuReorderTab';

// Upsells Management
import UpsellsTab from '@/components/admin/food/UpsellsTab';
import DisplayManagementTab from '@/components/admin/food/DisplayManagementTab';

// Marketing & Campaigns
import {
  CampaignsTab,
  AutomatedCampaigns,
  PromoCodesTab,
  SMSTab,
  SmsOptOutTab,
  ViralMarketingTab
} from '@/components/admin/marketing';
import ReferralCampaignTab from '@/components/admin/marketing/ReferralCampaignTab';

// Email Automation
import EmailAutomationTab from '@/components/admin/EmailAutomationTab';

// Loyalty & Customers
import {
  FoundingMembersTab,
  PartnerManagementTab,
  SalesRepsTab,
  ClientLookup
} from '@/components/admin/loyalty';
import ReferralPartnersTab from '@/components/admin/loyalty/ReferralPartnersTab';

// Inventory & Menu
import {
  InventoryTab,
  MenuItemModal,
  CategoryManager,
  CustomizationsTab,
  PopularCustomizationsTab,
  BuildYourOwnTab,
  SpecialsTab,
  CostManagementTab
} from '@/components/admin/inventory';

// Settings & Configuration
import {
  SettingsTab,
  AdminUsersTab,
  ContactSettingsTab,
  KioskSettingsTab,
  AlertsTab,
  ActivityLogTab,
  AIVerificationTab,
  QRCodeTab,
  StaffManagementTab,
  ClearTestDataButton
} from '@/components/admin/settings';

// WiFi Settings
import WifiSettingsTab from '@/components/admin/marketing/WifiSettingsTab';

// Coffee Pass
import CoffeeClubTab from '@/components/admin/CoffeeClubTab';

// Catering Management
import AdminCateringPage from '@/pages/admin/AdminCateringPage';

// FRCS Fiscal Dashboard
import FiscalDashboard from '@/components/admin/FiscalDashboard';

// Finance Management
import {
  SalesReconciliationTab,
  SalesReportTab,
  ShiftReconciliationTab,
  ExpenseTrackingTab,
  VendorManagementTab,
  DetailedInventoryTab,
  BusinessValuationTab,
  StartupInvestmentTab,
  InvestorReadyTab,
  OperationsDashboardTab,
  CancellationsReportTab,
  UnifiedTransactionsTab,
  MPaisaReconciliationTab,
  MistakeLossReportTab
} from '@/components/admin/finance';
import DataReconciliationTab from '@/components/admin/finance/DataReconciliationTab';

// Content & CMS
import {
  SimpleCMS,
  MediaLibrary,
  LegalPagesTab,
  LayoutDesignerTab
} from '@/components/admin/content';

// Role Permissions
import { RolePermissionsTab } from '@/components/admin/cms';

// Manager Override Report
import ManagerOverrideReport from '@/components/admin/reporting/ManagerOverrideReport';

// Staff Performance Reviews
import StaffPerformanceTab from '@/components/admin/reporting/StaffPerformanceTab';

// Menu Item Performance
import MenuItemPerformanceTab from '@/components/admin/analytics/MenuItemPerformanceTab';

// Owner Order Audit
import OwnerOrderAuditPage from '@/pages/admin/OwnerOrderAuditPage';

// Device & Station Management
import {
  DeviceManagementTab,
  StaffPinManagerTab,
  TableManagementTab,
  OrderTimingMetricsTab,
  SupervisorSOPTab
} from '@/components/admin/devices';

// Workforce Management
import {
  TimeClockTab,
  SchedulingTab,
  PTOTab,
  PayrollManagementTab
} from '@/components/admin/workforce';

// Shared
import {
  ContactsTab,
  MessagesTab,
  QuickStatsCard,
  LiveTransactionFlow
} from '@/components/admin/shared';

// Loading fallback
function TabLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Access Denied component
function AccessDenied({ tabName }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-destructive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-3V9m0 0V7m0 2h2m-2 0H9" />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-foreground mb-2">Access Denied</h3>
      <p className="text-muted-foreground max-w-md">
        You don&apos;t have permission to access {tabName || 'this section'}. 
        Contact your administrator to request access.
      </p>
    </div>
  );
}

function AdminContent({
  activeTab,
  admin,
  authHeaders,
  menuItems,
  orders,
  messages,
  adminUsers,
  categories,
  categoryLabels,
  fetchData,
  onEditMenuItem,
  onAddMenuItem,
  handleCategoriesChange
}) {
  const { canAccessTab, isOwner } = usePermissions();

  // Map of tab IDs to their parent sections (for permission checking)
  const tabSectionMap = {
    'dashboard': null,
    'menu': 'food',
    'orders': 'food',
    'build-your-own': 'food',
    'ai-verification': 'food',
    'customizations': 'food',
    'popular-customizations': 'food',
    'specials': 'food',
    'inventory': 'food',
    'floor-status': 'food',
    'alerts': 'food',
    'cart-recovery': 'food',
    'upsells': 'food',
    'display-management': 'food',
    'contacts': 'cms',
    'founding-members': 'cms',
    'coffee-club': 'cms',
    'catering': 'cms',
    'staff-management': 'cms',
    'partners': 'cms',
    'sales-reps': 'cms',
    'referral-partners': 'cms',
    'users': 'cms',
    'role-permissions': 'cms',
    'receipt-settings': 'settings',
    'kiosk-settings': 'settings',
    'printer-routing': 'settings',
    'kitchen-display': 'settings',
    'cash-drawer': 'settings',
    'alert-notifications': 'settings',
    'email-notifications': 'settings',
    'sms': 'settings',
    'sms-optout': 'settings',
    'investor-ready': 'finance',
    'operations-dashboard': 'finance',
    'cost-control': 'finance',
    'startup-investment': 'finance',
    'shift-reconciliation': 'finance',
    'expense-tracking': 'finance',
    'vendor-management': 'finance',
    'detailed-inventory': 'finance',
    'business-valuation': 'finance',
    'cancellations': 'finance',
    'mistake-losses': 'finance',
    'data-cleanup': 'finance',
    'unified-sales': 'finance',
    'owner-audit': 'finance',
    'viral-marketing': 'marketing',
    'referral-campaign': 'marketing',
    'campaigns': 'marketing',
    'automated-campaigns': 'marketing',
    'email-automation': 'marketing',
    'aeo-checklist': 'marketing',
    'google-reviews': 'marketing',
    'promo-codes': 'marketing',
    'qrcode': 'marketing',
    'messages': 'marketing',
    'legal-pages': 'content',
    'contact-settings': 'content',
    'blog': 'content',
    'layout-designer': 'content',
    'media': 'content',
    'daily-insights': 'reporting',
    'analytics': 'reporting',
    'fiscal-dashboard': 'reporting',
    'accounting-export': 'reporting',
    'comparative-reports': 'reporting',
    'automated-reports': 'reporting',
    'profitability': 'reporting',
    'manager-overrides': 'reporting',
    'royalty-report': 'reporting',
    'activity-log': 'reporting',
    'staff-performance': 'cms',
    'device-management': 'stations',
    'staff-pins': 'stations',
    'table-management': 'stations',
    'order-timing': 'stations',
    'supervisor-sop': 'stations',
    'time-clock': 'workforce',
    'scheduling': 'workforce',
    'time-off': 'workforce',
    'payroll': 'workforce',
    'mpaisa-reconciliation': 'finance',
  };

  // Check if user has access to current tab
  const parentSection = tabSectionMap[activeTab];
  const hasAccess = canAccessTab(activeTab, parentSection);

  // Special case: role-permissions is owner-only
  if (activeTab === 'role-permissions' && !isOwner) {
    return <AccessDenied tabName="Role Permissions" />;
  }

  // Special case: royalty-report is owner-only
  if (activeTab === 'royalty-report' && !isOwner) {
    return <AccessDenied tabName="Royalty Report" />;
  }

  // General permission check
  if (!hasAccess && !isOwner) {
    return <AccessDenied tabName={activeTab} />;
  }

  // Render the appropriate tab content
  return (
    <Suspense fallback={<TabLoading />}>
      {activeTab === 'dashboard' && (
        <DashboardTab 
          menuItems={menuItems} 
          orders={orders} 
          messages={messages}
          authHeaders={authHeaders}
          admin={admin}
        />
      )}
      {activeTab === 'daily-insights' && (
        <DailyInsightsDashboard authHeaders={authHeaders} />
      )}
      {activeTab === 'analytics' && (
        <UnifiedAnalytics />
      )}
      {activeTab === 'fiscal-dashboard' && (
        <FiscalDashboard />
      )}
      {activeTab === 'email-automation' && (
        <EmailAutomationTab />
      )}
      {activeTab === 'aeo-checklist' && (
        <AeoChecklistTab />
      )}
      {activeTab === 'blog' && (
        <BlogTab />
      )}
      {activeTab === 'google-reviews' && (
        <GoogleReviewsTab />
      )}
      {activeTab === 'automated-campaigns' && (
        <AutomatedCampaigns />
      )}
      {activeTab === 'activity-log' && (
        <ActivityLogTab currentUser={admin} />
      )}
      {activeTab === 'manager-overrides' && (
        <ManagerOverrideReport />
      )}
      {activeTab === 'legal-pages' && (
        <LegalPagesTab token={admin?.access_token} />
      )}
      {activeTab === 'content' && (
        <SimpleCMS />
      )}
      {activeTab === 'media' && (
        <div className="bg-card rounded-2xl p-6">
          <h2 className="font-heading text-2xl mb-6 text-foreground">Media Library</h2>
          <MediaLibrary selectionMode={false} />
        </div>
      )}
      {activeTab === 'menu' && (
        <MenuTab 
          items={menuItems} 
          categories={categories}
          categoryLabels={categoryLabels}
          onRefresh={fetchData}
          onEdit={onEditMenuItem}
          onAdd={onAddMenuItem}
          onCategoriesChange={handleCategoriesChange}
        />
      )}
      {activeTab === 'build-your-own' && (
        <BuildYourOwnTab />
      )}
      {activeTab === 'specials' && (
        <SpecialsTab menuItems={menuItems} />
      )}
      {activeTab === 'customizations' && (
        <CustomizationsTab />
      )}
      {activeTab === 'popular-customizations' && (
        <PopularCustomizationsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'inventory' && (
        <InventoryTab />
      )}
      {activeTab === 'alerts' && (
        <AlertsTab />
      )}
      {activeTab === 'founding-members' && (
        <FoundingMembersTab admin={admin} />
      )}
      {activeTab === 'coffee-club' && (
        <CoffeeClubTab authHeaders={authHeaders} />
      )}
      {activeTab === 'catering' && (
        <AdminCateringPage authHeaders={authHeaders} />
      )}
      {activeTab === 'contact-settings' && (
        <ContactSettingsTab />
      )}
      {activeTab === 'campaigns' && (
        <CampaignsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'viral-marketing' && (
        <ViralMarketingTab />
      )}
      {activeTab === 'referral-campaign' && (
        <ReferralCampaignTab authHeaders={authHeaders} />
      )}
      {activeTab === 'partners' && (
        <PartnerManagementTab />
      )}
      {activeTab === 'sales-reps' && (
        <SalesRepsTab />
      )}
      {activeTab === 'referral-partners' && (
        <ReferralPartnersTab />
      )}
      {activeTab === 'promo-codes' && (
        <PromoCodesTab authHeaders={authHeaders} />
      )}
      {activeTab === 'sms' && (
        <SMSTab />
      )}
      {activeTab === 'sms-optout' && (
        <SmsOptOutTab admin={admin} />
      )}
      {activeTab === 'cart-recovery' && (
        <CartRecoveryTab admin={admin} />
      )}
      {activeTab === 'upsells' && (
        <UpsellsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'display-management' && (
        <DisplayManagementTab />
      )}
      {activeTab === 'orders' && (
        <OrdersTab orders={orders} onRefresh={fetchData} />
      )}
      {activeTab === 'receipt-settings' && (
        <ReceiptSettingsTab />
      )}
      {activeTab === 'floor-status' && (
        <TableStatusTab authHeaders={authHeaders} />
      )}
      {activeTab === 'kitchen-display' && (
        <KitchenDisplayTab authHeaders={authHeaders} />
      )}
      {activeTab === 'printer-routing' && (
        <PrinterRoutingTab authHeaders={authHeaders} />
      )}
      {activeTab === 'cash-drawer' && (
        <CashDrawerTab authHeaders={authHeaders} />
      )}
      {activeTab === 'staff-management' && (
        <StaffManagementTab authHeaders={authHeaders} />
      )}
      {activeTab === 'staff-performance' && (
        <StaffPerformanceTab currentUser={admin} />
      )}
      {activeTab === 'ai-verification' && (
        <AIVerificationTab authHeaders={authHeaders} />
      )}
      {activeTab === 'accounting-export' && (
        <AccountingExportTab authHeaders={authHeaders} />
      )}
      {activeTab === 'comparative-reports' && (
        <ComparativeReportingTab authHeaders={authHeaders} />
      )}
      {activeTab === 'automated-reports' && (
        <AutomatedReportsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'profitability' && (
        <ProfitabilityTab authHeaders={authHeaders} />
      )}
      {activeTab === 'royalty-report' && isOwner && (
        <RoyaltyReportTab authHeaders={authHeaders} />
      )}
      {activeTab === 'kiosk-settings' && (
        <KioskSettingsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'wifi-settings' && (
        <WifiSettingsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'alert-notifications' && (
        <AlertNotificationsTab authHeaders={authHeaders} adminRole={admin?.role} />
      )}
      {activeTab === 'email-notifications' && (
        <NotificationSettingsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'layout-designer' && (
        <LayoutDesignerTab authHeaders={authHeaders} />
      )}
      {activeTab === 'contacts' && (
        <ContactsTab authHeaders={authHeaders} currentAdmin={admin} />
      )}
      {activeTab === 'messages' && (
        <MessagesTab messages={messages} onRefresh={fetchData} />
      )}
      {activeTab === 'users' && (
        <AdminUsersTab 
          users={adminUsers} 
          onRefresh={fetchData}
          currentAdmin={admin}
        />
      )}
      {activeTab === 'role-permissions' && isOwner && (
        <RolePermissionsTab authHeaders={authHeaders} />
      )}
      {activeTab === 'settings' && (
        <SettingsTab />
      )}
      {activeTab === 'qrcode' && (
        <QRCodeTab />
      )}
      {activeTab === 'startup-investment' && (
        <StartupInvestmentTab />
      )}
      {activeTab === 'investor-ready' && (
        <InvestorReadyTab />
      )}
      {activeTab === 'operations-dashboard' && (
        <OperationsDashboardTab />
      )}
      {activeTab === 'cost-control' && (
        <CostManagementTab authHeaders={authHeaders} />
      )}
      {activeTab === 'shift-reconciliation' && (
        <ShiftReconciliationTab />
      )}
      {activeTab === 'data-cleanup' && (
        <DataReconciliationTab />
      )}
      {activeTab === 'cancellations' && (
        <CancellationsReportTab />
      )}
      {activeTab === 'mistake-losses' && (
        <MistakeLossReportTab />
      )}
      {activeTab === 'unified-sales' && (
        <UnifiedTransactionsTab />
      )}
      {activeTab === 'owner-audit' && (
        <OwnerOrderAuditPage />
      )}
      {activeTab === 'mpaisa-reconciliation' && (
        <MPaisaReconciliationTab />
      )}
      {activeTab === 'sales-reconciliation' && (
        <SalesReconciliationTab />
      )}
      {activeTab === 'sales-report' && (
        <SalesReportTab />
      )}
      {activeTab === 'menu-performance' && (
        <MenuItemPerformanceTab />
      )}
      {activeTab === 'expense-tracking' && (
        <ExpenseTrackingTab />
      )}
      {activeTab === 'vendor-management' && (
        <VendorManagementTab />
      )}
      {activeTab === 'detailed-inventory' && (
        <DetailedInventoryTab />
      )}
      {activeTab === 'business-valuation' && (
        <BusinessValuationTab />
      )}
      {activeTab === 'device-management' && (
        <DeviceManagementTab />
      )}
      {activeTab === 'staff-pins' && (
        <StaffPinManagerTab token={admin?.access_token} />
      )}
      {activeTab === 'table-management' && (
        <TableManagementTab token={admin?.access_token} />
      )}
      {activeTab === 'order-timing' && (
        <OrderTimingMetricsTab token={admin?.access_token} />
      )}
      {activeTab === 'supervisor-sop' && (
        <SupervisorSOPTab token={admin?.access_token} />
      )}
      {activeTab === 'time-clock' && (
        <TimeClockTab token={admin?.access_token} />
      )}
      {activeTab === 'scheduling' && (
        <SchedulingTab token={admin?.access_token} />
      )}
      {activeTab === 'time-off' && (
        <PTOTab token={admin?.access_token} />
      )}
      {activeTab === 'payroll' && (
        <PayrollManagementTab token={admin?.access_token} adminRole={admin?.role} />
      )}
    </Suspense>
  );
}

// Import AlertNotificationsTab separately since it's from settings
import AlertNotificationsTab from '@/components/admin/settings/AlertNotificationsTab';
import NotificationSettingsTab from '@/components/admin/settings/NotificationSettingsTab';

// Dashboard Tab Component (kept inline for simplicity)
function DashboardTab({ menuItems, orders, messages, authHeaders, admin }) {
  // Filter for actual pending orders (not paid, not cancelled)
  const pendingOrders = orders.filter(o => 
    o.status === 'pending' && 
    o.payment_status !== 'paid' && 
    o.payment_status !== 'cancelled'
  ).length;
  
  const unreadMessages = messages.filter(m => !m.is_read).length;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl text-foreground">Dashboard</h1>
      </div>
      
      {/* Quick Stats Cards - This shows accurate sales data from API */}
      <QuickStatsCard authHeaders={authHeaders} />

      {/* Client Lookup Section */}
      <div className="mb-8">
        <ClientLookup authHeaders={authHeaders} />
      </div>

      {/* Two Column Layout: Transaction Flow + Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Live Transaction Flow Widget */}
        <LiveTransactionFlow authHeaders={authHeaders} />

        {/* Recent Orders */}
        <div className="bg-card rounded-2xl shadow-sm p-6 border border-border">
          <h2 className="font-heading text-xl mb-4 text-foreground">Recent Orders</h2>
          <div className="space-y-3">
            {orders.slice(0, 5).map(order => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-muted rounded-xl">
                <div>
                  <p className="font-medium text-foreground">{order.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{order.items?.length || 0} items • ${order.total?.toFixed(2) || '0.00'}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                  order.status === 'confirmed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                  order.status === 'preparing' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' :
                  order.status === 'ready' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                  order.status === 'completed' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {order.status}
                </span>
              </div>
            ))}
            {orders.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <p>No recent orders</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Menu Tab Component (kept inline for simplicity)
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, ArrowUpDown, Edit, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

function MenuTab({ items, categories, categoryLabels, onRefresh, onEdit, onAdd, onCategoriesChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCategory = searchParams.get('category');
  const [filter, setFilter] = useState(urlCategory || 'all');
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showReorderMode, setShowReorderMode] = useState(false);

  useEffect(() => {
    if (urlCategory && urlCategory !== filter) {
      setFilter(urlCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCategory]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    const newParams = new URLSearchParams(searchParams);
    if (newFilter === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', newFilter);
    }
    setSearchParams(newParams, { replace: true });
  };

  const drinksCategories = categories.filter(c => 
    ['iced-drinks', 'smoothies-blends', 'fresh-mixed-juice', 'milk-shakes'].includes(c.id)
  ).map(c => c.id);

  const categoryOrder = {};
  categories.forEach((cat, index) => {
    categoryOrder[cat.id] = cat.order !== undefined ? cat.order : index;
  });

  const sortedItems = [...items].sort((a, b) => {
    const orderA = categoryOrder[a.category] ?? 999;
    const orderB = categoryOrder[b.category] ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name || '').localeCompare(b.name || '');
  });

  const filteredItems = filter === 'all' 
    ? sortedItems 
    : filter === 'drinks' 
      ? sortedItems.filter(i => drinksCategories.includes(i.category))
      : sortedItems.filter(i => i.category === filter);

  const getDefaultCategory = () => {
    if (filter === 'all' || filter === 'drinks') return categories[0]?.id || 'coffee';
    return filter;
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/admin/menu/${id}`);
      toast.success('Item deleted');
      onRefresh();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await api.put(`/admin/menu/${item.id}`, { is_available: !item.is_available });
      toast.success(item.is_available ? 'Item marked unavailable' : 'Item marked available');
      onRefresh();
    } catch (err) {
      toast.error('Failed to update item');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-heading text-3xl text-foreground">Menu Management</h1>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowReorderMode(!showReorderMode)}
            className="rounded-full"
            data-testid="reorder-items-btn"
          >
            <ArrowUpDown className="w-4 h-4 mr-2" />
            {showReorderMode ? 'Exit Reorder' : 'Reorder Items'}
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowCategoryManager(!showCategoryManager)}
            className="rounded-full"
            data-testid="manage-categories-btn"
          >
            {showCategoryManager ? 'Hide Categories' : 'Manage Categories'}
          </Button>
          <Button 
            onClick={() => onAdd(getDefaultCategory())}
            className="rounded-full bg-primary hover:bg-primary/90"
            data-testid="add-menu-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>
      </div>

      {showReorderMode && (
        <div className="mb-6">
          <MenuReorderTab 
            items={items} 
            categories={categories} 
            categoryLabels={categoryLabels}
            onRefresh={onRefresh}
          />
        </div>
      )}

      {!showReorderMode && showCategoryManager && (
        <div className="mb-6">
          <CategoryManager onCategoriesChange={onCategoriesChange} />
        </div>
      )}

      {!showReorderMode && (
        <>
          <div className="mb-6">
            <div className="flex flex-wrap gap-2 mb-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-primary text-white' 
                    : 'bg-card hover:bg-muted text-foreground border border-border'
                }`}
              >
                All ({items.length})
              </button>
              {categories.filter(c => c.is_active !== false).map(cat => {
                const count = items.filter(i => i.category === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleFilterChange(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      filter === cat.id 
                        ? 'bg-primary text-white' 
                        : 'bg-card hover:bg-muted text-foreground border border-border'
                    }`}
                  >
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map(item => (
              <div 
                key={item.id} 
                className={`bg-card rounded-2xl overflow-hidden shadow-sm border border-border ${!item.is_available && 'opacity-60'}`}
                data-testid={`admin-menu-item-${item.id}`}
              >
                <div className="aspect-video relative">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  {!item.is_available && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white font-medium">Unavailable</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {item.is_featured && (
                      <span className="px-2 py-1 bg-primary text-white text-xs rounded-full">Featured</span>
                    )}
                    {item.is_bestseller && (
                      <span className="px-2 py-1 bg-accent text-white text-xs rounded-full">Bestseller</span>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-foreground">{item.name}</h3>
                      <p className="text-sm text-muted-foreground">{categoryLabels[item.category] || item.category}</p>
                    </div>
                    <p className="font-medium text-primary">${item.price.toFixed(2)}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">{item.description}</p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1 rounded-lg"
                      onClick={() => onEdit(item)}
                      data-testid={`edit-menu-${item.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="rounded-lg"
                      onClick={() => toggleAvailability(item)}
                    >
                      {item.is_available ? <X className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="rounded-lg text-destructive hover:bg-destructive hover:text-white"
                      onClick={() => handleDelete(item.id)}
                      data-testid={`delete-menu-${item.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <p>No items in this category yet.</p>
              <Button 
                className="mt-4 rounded-full"
                onClick={() => onAdd(filter === 'all' ? 'coffee' : filter)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Item
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default memo(AdminContent);
